import {Three as THREE} from "@cm/material-nodes/three"

// uses approach from Fast and Accurate Parametric Curve Length Computation, Stephen Vincent and David Forsey
// DOI: 10.1080/10867651.2001.10487548
// https://web.archive.org/web/20100109033413/http://jgt.akpeters.com/papers/VincentForsey01/

type LengthAtT = [number, number]

export class ParametricCurve extends THREE.CatmullRomCurve3 {
    cacheCurveLengthAtT: LengthAtT[] | undefined = undefined
    cacheCurveLength: number[] | undefined = undefined
    needsUpdate: boolean = false

    override getLength(): number {
        const cacheCurveLength = this.getLengths()
        return cacheCurveLength[cacheCurveLength.length - 1]
    }

    override getLengths(divisions = this.arcLengthDivisions) {
        if (this.needsUpdate || this.cacheCurveLength === undefined) {
            const curveLengthAtT = this.getLengthAtTForCurve(divisions)
            this.cacheCurveLength = curveLengthAtT.map(([len, t]) => len)
        }
        return this.cacheCurveLength
    }

    override getUtoTmapping(u: number, distance: number): number {
        const curveLengthAtT = this.getLengthAtTForCurve(this.arcLengthDivisions)

        let targetArcLength

        if (distance) {
            targetArcLength = distance
        } else {
            targetArcLength = u * curveLengthAtT[curveLengthAtT.length - 1][0]
        }

        // binary search for the index with largest value smaller than target u distance
        let i = 0
        const il = curveLengthAtT.length
        let low = 0
        let high = il - 1
        let comparison

        while (low <= high) {
            // less likely to overflow, though probably not issue here, JS doesn't really have integers, all numbers are floats
            i = Math.floor(low + (high - low) / 2)
            comparison = curveLengthAtT[i][0] - targetArcLength
            if (comparison < 0) {
                low = i + 1
            } else if (comparison > 0) {
                high = i - 1
            } else {
                high = i
                break
            }
        }

        i = high
        if (curveLengthAtT[i][0] === targetArcLength) {
            return curveLengthAtT[i][1]
        }

        const beforeLengthAtT = curveLengthAtT[i]
        const afterLengthAtT = curveLengthAtT[i + 1]
        const segmentFraction = (targetArcLength - beforeLengthAtT[0]) / (afterLengthAtT[0] - beforeLengthAtT[0])
        return beforeLengthAtT[1] + segmentFraction * (afterLengthAtT[1] - beforeLengthAtT[1])
    }

    getLengthAtTForCurve(divisions: number): LengthAtT[] {
        if (this.needsUpdate || this.cacheCurveLengthAtT === undefined) {
            this.cacheCurveLengthAtT = this.generateLengthAtTForCurve(divisions)
        }
        this.needsUpdate = false
        return this.cacheCurveLengthAtT
    }

    generateLengthAtTForCurve(segments: number): LengthAtT[] {
        // 3 ref points required per segments
        // with exception of the first one, every segments shares the first ref point with the previous one
        const refPointsCount = 1 + segments * 2
        const refPoints = []
        for (let i = 0; i < refPointsCount; i++) {
            const t = i / (refPointsCount - 1)
            const p = this.getPoint(t)
            refPoints.push({t, p})
        }

        const lengthAtT: LengthAtT[] = [[0, 0]]
        for (let i = 0; i < refPoints.length - 1; i += 2) {
            const segmentLengthAtTList = this.generateLengthAtTForCurveSegment(
                refPoints[i].t,
                refPoints[i + 1].t,
                refPoints[i + 2].t,
                refPoints[i].p,
                refPoints[i + 1].p,
                refPoints[i + 2].p,
            )

            const lastLengthAtT = lengthAtT[lengthAtT.length - 1]
            segmentLengthAtTList.forEach(([len, t]) => lengthAtT.push([len + lastLengthAtT[0], t + lastLengthAtT[1]]))
        }

        return lengthAtT
    }

    generateLengthAtTForCurveSegment(t0: number, t1: number, t2: number, p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector3): LengthAtT[] {
        const kEpsilon = 1e-5
        const kEpsilon2 = 1e-6
        const kMaxArc = 1.01
        const kLenRatio = 1.01

        const d1 = p0.distanceTo(p2)
        const da = p0.distanceTo(p1)
        const db = p1.distanceTo(p2)
        const d2 = da + db

        if (d2 < kEpsilon) return [[d2 + (d2 - d1) / 3, t2 - t0]]

        if (d1 < kEpsilon || d2 / d1 > kMaxArc || da < kEpsilon2 || db / da > kLenRatio || db < kEpsilon2 || da / db > kLenRatio) {
            const t01 = (t0 + t1) / 2
            const t12 = (t1 + t2) / 2

            const p01 = this.getPoint(t01)
            const p12 = this.getPoint(t12)

            const lengthAtT01 = this.generateLengthAtTForCurveSegment(t0, t01, t1, p0, p01, p1)
            const lengthAtT12 = this.generateLengthAtTForCurveSegment(t1, t12, t2, p1, p12, p2)

            const len01 = lengthAtT01[lengthAtT01.length - 1][0]
            return [...lengthAtT01, ...lengthAtT12.map(([len, t]) => [len + len01, t + t1 - t0] as LengthAtT)]
        }

        return [[d2 + (d2 - d1) / 3, t2 - t0]]
    }
}
